import React, { useState, useEffect, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import NotificationCenter from './NotificationCenter';
import LanguageSelector from './LanguageSelector';
import { getCarouselImages, pexelsImages } from '../config/pexels-images';
import { getImagePath } from '../utils/imagePath';

const dropLinkClass = (active) =>
  `block px-4 py-2.5 text-sm font-medium rounded-lg mx-1 ${
    active ? 'bg-gold-100 text-navy-900' : 'text-gray-700 hover:bg-gray-50'
  }`;

function NavDropdown({ menuId, label, active, openNavMenu, setOpenNavMenu, children }) {
  const open = openNavMenu === menuId;
  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpenNavMenu(open ? null : menuId)}
        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap border ${
          active || open
            ? 'bg-gold-500 text-white border-gold-600 shadow-md'
            : 'text-gray-700 hover:bg-gray-100 border-transparent'
        }`}
      >
        {label}
        <svg
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 min-w-[14rem] max-h-[min(70vh,24rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white py-2 shadow-xl z-[200] ring-1 ring-black/5"
          role="menu"
        >
          {children}
        </div>
      )}
    </div>
  );
}

const Header = memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openNavMenu, setOpenNavMenu] = useState(null);
  const [typedText, setTypedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [forceOffline, setForceOffline] = useState(false); // Pour forcer le mode offline (test)
  
  const fullText = 'Golden Mali SARL';
  
  const handleLogout = () => {
    logout();
    navigate('/'); // Rediriger vers la page Landing publique
  };
  
  // Effet de frappe au clavier - se déclenche une seule fois au montage
  useEffect(() => {
    let currentIndex = 0;
    setTypedText(''); // Réinitialiser au début
    
    const typingInterval = setInterval(() => {
      if (currentIndex < fullText.length) {
        setTypedText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 100); // Vitesse de frappe (100ms par lettre)
    
    return () => clearInterval(typingInterval);
  }, []); // Se déclenche uniquement au montage du composant
  
  // Curseur clignotant
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530); // Vitesse de clignotement
    
    return () => clearInterval(cursorInterval);
  }, []);

  // Images locales depuis assets/logo - identiques au site web
  // Utilise les images locales (1.webp à 5.webp) pour correspondre exactement au site
  const carouselImages = getCarouselImages(false); // false = utiliser images locales (identiques au site web)

  // Rotation automatique du carrousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
    }, 5000); // Change toutes les 5 secondes

    return () => clearInterval(interval);
  }, []);

  // Détecter le statut de connexion
  useEffect(() => {
    const updateOnlineStatus = () => {
      // Si forceOffline est activé, rester en mode offline
      setIsOnline(forceOffline ? false : navigator.onLine);
    };

    updateOnlineStatus();
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, [forceOffline]);

  // Toggle mode offline (pour tests)
  const toggleOfflineMode = () => {
    setForceOffline(!forceOffline);
    // Mettre à jour le statut immédiatement
    setIsOnline(forceOffline);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const path = location.pathname;
  const isOrSection =
    path === '/transaction/new' ||
    path.startsWith('/transactions') ||
    path === '/barres' ||
    path === '/factures' ||
    path === '/raffinage';
  const isCashSection = path.startsWith('/financial-flows') || path === '/swift';
  const isPilotageSection =
    path === '/dashboard' ||
    path === '/positions' ||
    path === '/stock' ||
    path === '/margins' ||
    path === '/reports';
  const isErpSection =
    path === '/comptabilite' ||
    path === '/archives-documents' ||
    path === '/parametres' ||
    path === '/extensions';

  useEffect(() => {
    setOpenNavMenu(null);
  }, [path]);

  return (
    <header className="relative">
      {/* Navbar moderne style SaaS - EN PREMIER avec z-index élevé */}
      <nav className="bg-white shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-[100]" role="navigation" aria-label="Navigation principale">
        <div className="max-w-[100%] mx-auto px-2 sm:px-3 lg:px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo - Aligné à gauche et compact */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/home" className="flex items-center space-x-1.5 no-underline">
                <div className="relative h-9 w-9 flex-shrink-0">
                  <img 
                    src={getImagePath("/assets/logo/GM.png")} 
                    alt="Golden Mali SARL Logo" 
                    className="h-9 w-9 object-contain"
                    onError={(e) => {
                      // Fallback si le logo n'existe pas
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement.querySelector('.logo-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div 
                    className="logo-fallback h-9 w-9 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg items-center justify-center shadow-lg"
                    style={{ display: 'none' }}
                  >
                    <span className="text-white font-black text-base">G</span>
                  </div>
                </div>
                <div className="flex flex-col justify-center leading-tight hidden 2xl:block">
                  <div className="text-xs font-bold text-navy-800 whitespace-nowrap">Golden Mali SARL</div>
                  <div className="text-[10px] text-gray-500 whitespace-nowrap">Plateforme SaaS</div>
                </div>
              </Link>
            </div>

            {/* Navigation principale — menus déroulants pour éviter la surcharge */}
            <div className="hidden lg:flex items-center gap-2 flex-1 justify-center min-w-0 mx-2 flex-wrap">
              <Link
                to="/home"
                className={`px-2.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-1 ${
                  isActive('/home')
                    ? 'bg-gold-500 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                aria-current={isActive('/home') ? 'page' : undefined}
              >
                {t('navigation.home')}
              </Link>

              <NavDropdown
                menuId="or"
                label="Or & opérations"
                active={isOrSection}
                openNavMenu={openNavMenu}
                setOpenNavMenu={setOpenNavMenu}
              >
                <Link
                  to="/transaction/new"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/transaction/new'))}
                >
                  {t('navigation.transaction')}
                </Link>
                <Link
                  to="/transactions"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(path.startsWith('/transactions'))}
                >
                  {t('navigation.transactions')}
                </Link>
                <Link
                  to="/barres"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/barres'))}
                >
                  Barres
                </Link>
                <Link
                  to="/factures"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/factures'))}
                >
                  Factures
                </Link>
                <Link
                  to="/raffinage"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/raffinage'))}
                >
                  Raffinage
                </Link>
              </NavDropdown>

              <NavDropdown
                menuId="cash"
                label="Cash & banque"
                active={isCashSection}
                openNavMenu={openNavMenu}
                setOpenNavMenu={setOpenNavMenu}
              >
                <Link
                  to="/financial-flows"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(
                    isActive('/financial-flows') || isActive('/financial-flows/dashboard')
                  )}
                >
                  Flux Financiers
                </Link>
                <Link
                  to="/swift"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/swift'))}
                >
                  SWIFT
                </Link>
              </NavDropdown>

              <NavDropdown
                menuId="pilotage"
                label="Pilotage"
                active={isPilotageSection}
                openNavMenu={openNavMenu}
                setOpenNavMenu={setOpenNavMenu}
              >
                <Link
                  to="/dashboard"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/dashboard'))}
                >
                  {t('navigation.dashboard')}
                </Link>
                <Link
                  to="/positions"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/positions'))}
                >
                  {t('navigation.positions')}
                </Link>
                <Link
                  to="/stock"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/stock'))}
                >
                  {t('navigation.stock')}
                </Link>
                <Link
                  to="/margins"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/margins'))}
                >
                  {t('navigation.margins')}
                </Link>
                <Link
                  to="/reports"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/reports'))}
                >
                  {t('navigation.reports')}
                </Link>
              </NavDropdown>

              <NavDropdown
                menuId="erp"
                label="Compta & fichiers"
                active={isErpSection}
                openNavMenu={openNavMenu}
                setOpenNavMenu={setOpenNavMenu}
              >
                <Link
                  to="/comptabilite"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/comptabilite'))}
                >
                  Compta
                </Link>
                <Link
                  to="/archives-documents"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/archives-documents'))}
                >
                  Archives
                </Link>
                <Link
                  to="/parametres"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/parametres'))}
                >
                  Paramètres
                </Link>
                <Link
                  to="/extensions"
                  role="menuitem"
                  onClick={() => setOpenNavMenu(null)}
                  className={dropLinkClass(isActive('/extensions'))}
                >
                  Extensions
                </Link>
              </NavDropdown>

              {isAuthenticated && (
                <Link
                  to="/admin"
                  className={`px-2.5 py-2 rounded-lg text-xs font-bold transition-all duration-200 whitespace-nowrap ${
                    isActive('/admin')
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-300'
                  }`}
                  title="Gestion des Partenaires - Super Admin"
                >
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {t('navigation.admin')}
                  </span>
                </Link>
              )}
            </div>
            
            {/* Notifications et Bouton Connexion/Déconnexion - à droite */}
            <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
              <LanguageSelector />
              {/* Indicateur et toggle de statut offline/online */}
              {isAuthenticated && (
                <button
                  onClick={toggleOfflineMode}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    isOnline 
                      ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200' 
                      : 'bg-gray-200 text-gray-700 border border-gray-400 hover:bg-gray-300'
                  }`}
                  title={isOnline ? 'Cliquer pour passer en mode hors ligne (test)' : 'Cliquer pour revenir en ligne'}
                >
                  <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                  <span className="hidden xl:inline">{isOnline ? 'En ligne' : 'Hors ligne'}</span>
                  {forceOffline && (
                    <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </button>
              )}
              {isAuthenticated && <NotificationCenter />}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className={`px-3 py-2 font-semibold text-xs rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 ${
                      isActive('/profile')
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    title={t('navigation.profile')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="hidden xl:inline">{user?.name || t('navigation.profile')}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold text-xs rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    {t('navigation.logout')}
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="px-3 py-2 bg-navy-800 hover:bg-navy-900 text-white font-semibold text-xs rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  {t('navigation.login')}
                </Link>
              )}
            </div>

            {/* Menu mobile / tablette (< lg) */}
            <div className="lg:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-navy-800 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 p-2 rounded-lg transition-all"
                aria-label="Menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Menu mobile déroulant */}
          {mobileMenuOpen && (
            <div id="mobile-menu" className="lg:hidden border-t border-gray-200 py-4 animate-fade-in max-h-[calc(100vh-4rem)] overflow-y-auto" role="menu">
              <div className="flex flex-col space-y-1 px-4">
                <Link
                  to="/home"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/home')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('navigation.home')}
                </Link>

                <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-500">Or & opérations</p>
                <Link
                  to="/transaction/new"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/transaction/new')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('navigation.transaction')}
                </Link>
                <Link
                  to="/transactions"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    path.startsWith('/transactions')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('navigation.transactions')}
                </Link>
                <Link
                  to="/barres"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/barres')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Barres
                </Link>
                <Link
                  to="/factures"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/factures')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Factures
                </Link>
                <Link
                  to="/raffinage"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/raffinage')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Raffinage
                </Link>

                <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-500">Cash & banque</p>
                <Link
                  to="/financial-flows"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/financial-flows') || isActive('/financial-flows/dashboard')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Flux Financiers
                </Link>
                <Link
                  to="/swift"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/swift')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  SWIFT
                </Link>

                <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-500">Pilotage</p>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/dashboard')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('navigation.dashboard')}
                </Link>
                <Link
                  to="/positions"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/positions')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('navigation.positions')}
                </Link>
                <Link
                  to="/stock"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/stock')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('navigation.stock')}
                </Link>
                <Link
                  to="/margins"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/margins')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('navigation.margins')}
                </Link>
                <Link
                  to="/reports"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isActive('/reports')
                      ? 'bg-gold-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('navigation.reports')}
                </Link>

                <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-500">Compta & fichiers</p>
                <Link to="/comptabilite" onClick={() => setMobileMenuOpen(false)} className={`px-4 py-2.5 rounded-lg font-semibold text-sm ${isActive('/comptabilite') ? 'bg-gold-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>Compta</Link>
                <Link to="/archives-documents" onClick={() => setMobileMenuOpen(false)} className={`px-4 py-2.5 rounded-lg font-semibold text-sm ${isActive('/archives-documents') ? 'bg-gold-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>Archives</Link>
                <Link to="/parametres" onClick={() => setMobileMenuOpen(false)} className={`px-4 py-2.5 rounded-lg font-semibold text-sm ${isActive('/parametres') ? 'bg-gold-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>Paramètres</Link>
                <Link to="/extensions" onClick={() => setMobileMenuOpen(false)} className={`px-4 py-2.5 rounded-lg font-semibold text-sm ${isActive('/extensions') ? 'bg-gold-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>Extensions</Link>
                
                {/* Lien Admin Mobile - visible uniquement si connecté */}
                {isAuthenticated && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-3 rounded-lg font-bold text-sm transition-all ${
                      isActive('/admin')
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200 border-2 border-purple-300'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {t('navigation.admin')}
                    </span>
                  </Link>
                )}
                
                {/* Sélecteur de langue Mobile */}
                <div className="px-4 py-2">
                  <LanguageSelector />
                </div>
                
                {/* Indicateur et toggle offline/online Mobile */}
                {isAuthenticated && (
                  <div className="px-4 py-2">
                    <button
                      onClick={toggleOfflineMode}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer hover:shadow-md ${
                        isOnline 
                          ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200' 
                          : 'bg-gray-200 text-gray-700 border border-gray-400 hover:bg-gray-300'
                      }`}
                      title={isOnline ? 'Cliquer pour passer en mode hors ligne (test)' : 'Cliquer pour revenir en ligne'}
                    >
                      <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                      <span>{isOnline ? 'En ligne' : 'Hors ligne'}</span>
                      {forceOffline && (
                        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
                {/* Notifications Mobile */}
                {isAuthenticated && (
                  <div className="px-4 py-2">
                    <NotificationCenter />
                  </div>
                )}
                
                {/* Bouton Connexion/Déconnexion Mobile */}
                {isAuthenticated ? (
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold text-sm rounded-lg transition-all mt-2"
                  >
                    {t('navigation.logout')}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 bg-navy-800 hover:bg-navy-900 text-white font-semibold text-sm rounded-lg transition-all mt-2"
                  >
                    {t('navigation.login')}
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
      

      {/* Carrousel d'images - EN DESSOUS - TRÈS AGRANDI avec z-index inférieur */}
      <div className="relative h-96 md:h-[450px] lg:h-[550px] xl:h-[650px] 2xl:h-[750px] overflow-hidden z-10">
        {/* Images du carrousel */}
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Gradient de fallback cohérent (or/bleu) - TOUJOURS visible en arrière-plan */}
            <div
              className="absolute inset-0"
              style={{
                background: image.fallbackGradient,
                zIndex: 1
              }}
            />
            {/* Image - locale avec fallback Pexels */}
            <img
              src={image.url}
              alt={image.title}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ zIndex: 2 }}
              loading="eager"
              onError={(e) => {
                // Si l'image locale ne charge pas, essayer Pexels en fallback
                if (image.url && !image.url.startsWith('http') && pexelsImages.carousel[index]?.url) {
                  e.target.src = pexelsImages.carousel[index].url;
                  return;
                }
                // Si aucune image ne charge, masquer et garder le gradient
                e.target.style.display = 'none';
                e.target.style.opacity = '0';
                console.warn(`Image carrousel non chargée, utilisation du gradient: ${image.title}`);
              }}
              onLoad={(e) => {
                // Si l'image charge correctement, l'afficher
                e.target.style.display = 'block';
                e.target.style.opacity = '1';
              }}
            />
            {/* Overlay sombre pour meilleure lisibilité */}
            <div className="absolute inset-0 bg-gradient-to-r from-navy-900/85 via-navy-800/75 to-navy-900/85" style={{ zIndex: 3 }}></div>
          </div>
        ))}
        
        {/* Contenu Hero superposé sur le carrousel - Texte et CTAs uniquement */}
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 md:mb-6 text-white drop-shadow-2xl hover:text-gold-300 transition-colors duration-300 cursor-default">
                <span className="font-mono">{typedText}</span>
                <span className={`inline-block w-1 md:w-1.5 h-10 md:h-14 lg:h-16 xl:h-20 bg-gold-400 ml-2 align-middle ${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 animate-pulse`}>|</span>
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl xl:text-3xl font-semibold text-gold-300 mb-3 md:mb-4 drop-shadow-lg hover:text-gold-200 transition-colors duration-300">
                {t('landing.subtitle')}
              </p>
              <p className="text-sm md:text-base lg:text-lg xl:text-xl text-gray-200 mb-8 md:mb-12 max-w-3xl mx-auto drop-shadow-md hover:text-white transition-colors duration-300">
                {t('landing.description')}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
                <Link
                  to="/dashboard"
                  className="btn-gold px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-bold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 active:scale-95"
                >
                  {t('landing.cta.dashboard')}
                </Link>
                <Link
                  to="/transaction/new"
                  className="btn-navy px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-bold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 active:scale-95"
                >
                  {t('landing.cta.transaction')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Indicateurs de pagination */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentImageIndex
                  ? 'w-8 bg-gold-500'
                  : 'w-2 bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Aller à l'image ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </header>
  );
});

Header.displayName = 'Header';

export default Header;
